/**
 * Клиент правового блока (E9): расчёт неустойки и процентов ст. 395, претензии, исковая давность,
 * ручные статусы. Типы согласованы с сервером ПО ЗНАЧЕНИЯМ (клиент и api типы напрямую не делят) —
 * формы из apps/api/src/legal/{calc,service,store}.ts.
 *
 * Весь расчёт делает сервер (ядро E4): здесь ни одного рубля не считается — только показ.
 * Роли нужны ТОЛЬКО чтобы не показывать заведомо запрещённую кнопку: настоящий запрет — серверный
 * (правило 5, строка 4.3 «Претензии, перевод в спор, снятие с контроля»).
 */

import type { Role } from "./roles";
import type { ApiResult } from "./contracts";
import type { Obligation } from "./obligations";

/** Расчётный период «как посчитано» (ТЗ 4.2.5): база, дни, ставка, сумма. */
export interface CalcPeriod {
  readonly from: string;
  readonly to: string;
  readonly days: number;
  readonly base: string;
  /** Сотые доли процента: в день (неустойка) / годовых (ст. 395). */
  readonly rate: number;
  readonly sum: string;
}

export interface AccrualView {
  readonly total: string;
  readonly days: number;
  readonly capped: boolean;
  readonly capAbs: string | null;
  /** Основание потолка, заданного процентом (CR-2а): «10 % от цены договора». Готовая строка. */
  readonly capSource: string | null;
  readonly ratePerDay: number | null;
  readonly periods: readonly CalcPeriod[];
}

export type NoticeCode =
  | "awaiting_event"
  | "no_due"
  | "no_amount"
  | "cap_base_missing"
  | "rate_not_set"
  | "rate_precision"
  | "rate_malformed"
  | "interest395_not_applicable"
  | "stopped_by_done_date"
  | "stopped_by_cancellation"
  | "cap_applied"
  | "due_shifted"
  | "accrual_due_overridden";

export interface CalcNotice {
  readonly code: NoticeCode;
  readonly message: string;
}

export interface SanctionCalc {
  readonly base: string | null;
  readonly dueComputed: string | null;
  readonly dueShifted: boolean;
  readonly dueUsed: string | null;
  readonly accrualOverride: { readonly date: string; readonly reason: string } | null;
  readonly from: string | null;
  readonly to: string | null;
  readonly stoppedByDoneDate: boolean;
  /** Начисление оборвано датой снятия обязательства с контроля (D-6, Р-31). */
  readonly stoppedByCancelled: boolean;
  /** Дата снятия с контроля (D-6). NULL — обязательство под контролем. */
  readonly cancelledDate: string | null;
  /** Периода начисления нет: последний день раньше первого (решает сервер, показывать словами). */
  readonly noAccrualPeriod: boolean;
  readonly penalty: AccrualView | null;
  readonly interest395: AccrualView | null;
  readonly primary: "penalty" | "interest395" | null;
  readonly notices: readonly CalcNotice[];
}

export interface Limitation {
  readonly limitDate: string | null;
  readonly daysLeft: number | null;
  readonly expired: boolean;
  readonly warn: boolean;
  readonly suspensionNote: string | null;
}

/** Результат рассмотрения претензии (значения = enum `ClaimResult` сервера). */
export type ClaimResult = "satisfied" | "partially_satisfied" | "rejected" | "no_answer";

export const CLAIM_RESULT_LABELS: Record<ClaimResult, string> = {
  satisfied: "Удовлетворена",
  partially_satisfied: "Удовлетворена частично",
  rejected: "Отклонена",
  no_answer: "Ответа не поступило",
};

export interface ClaimTerm {
  readonly answerRaw: string;
  readonly answerDue: string;
  readonly shifted: boolean;
  readonly expired: boolean;
  readonly mayGoToCourt: boolean;
}

export interface Claim {
  readonly id: string;
  readonly obligationId: string;
  readonly sentDate: string;
  readonly outNumber: string;
  readonly answerDays: number;
  readonly amount: string | null;
  readonly calcOn: string | null;
  readonly result: ClaimResult | null;
  readonly resultDate: string | null;
  readonly resultNote: string | null;
  readonly authorId: string;
  readonly authorName: string;
  readonly createdAt: string;
  /** Отмена (сторно-паттерн E8a): id отменяемой претензии; null — обычная запись. */
  readonly reversesClaimId: string | null;
  readonly reason: string | null;
  /** Отменена ли эта претензия. */
  readonly reversed: boolean;
  /** Срок ответа; null — у отменённых записей и у самих записей об отмене. */
  readonly term: ClaimTerm | null;
}

export interface ObligationLegal {
  readonly obligation: Obligation;
  readonly calc: SanctionCalc;
  readonly limitation: Limitation;
  readonly claims: readonly Claim[];
  readonly activeClaim: Claim | null;
  readonly claimRequired: boolean;
  readonly defaultAnswerDays: number;
}

export interface LegalTotals {
  readonly count: number;
  /** ОСТАТОК основного долга (за вычетом уже полученного), а не полная сумма обязательств. */
  readonly debt: string;
  readonly sanctions: string;
}

// Свода по ОДНОМУ договору (`ContractLegal` + `fetchContractLegal`) здесь больше нет: с E12 ни
// один экран не обходит договоры по одному — правовой свод по всем действующим приходит целиком
// из `lib/summary.ts` (Р-41). Маршрут на сервере остался (карточка договора им пользуется через
// свои данные), но клиентский помощник убран намеренно: пока он есть, следующий экран легко
// «вернётся» на путь из пятисот последовательных запросов.

// ── Печатная форма расчёта (E9a) ──────────────────────────────────────────────

/**
 * Модель печатного документа. Собирает её СЕРВЕР (`legal/printForm.ts`): здесь ничего не
 * считается и не сравнивается. Сверку со снимком суммы претензии (Р-30) сервер делает у себя и
 * при расхождении ВООБЩЕ НЕ ОТДАЁТ форму: печать блокируется отказом с обеими суммами в тексте
 * (пересмотр Р-30 от 06.08.2026 — документ уходит контрагенту с одной, верной суммой). Клиенту
 * поэтому и нечего показывать в самом листе: он показывает отказ сервера.
 */
export interface PrintParty {
  readonly name: string;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly address: string | null;
}

export interface PrintSection {
  readonly kind: "penalty" | "interest395";
  readonly title: string;
  readonly primary: boolean;
  readonly periods: readonly CalcPeriod[];
  readonly days: number;
  readonly total: string;
  /** Штамп потолка (Р-26): начислено по периодам `accrued`, итог ограничен `cap`. */
  readonly cap: {
    readonly accrued: string;
    readonly cap: string;
    readonly source: string | null;
  } | null;
}

export interface PrintClaimInfo {
  readonly outNumber: string;
  readonly sentDate: string;
  readonly answerDays: number;
  readonly amount: string | null;
  readonly calcOn: string | null;
}

export interface CalcPrintForm {
  readonly title: string;
  readonly org: {
    readonly name: string;
    readonly inn: string;
    readonly kpp: string;
    readonly ogrn: string;
    readonly address: string;
    readonly phone: string;
    readonly email: string;
  };
  /** Незаполненные обязательные реквизиты организации (задел E15). */
  readonly orgMissing: readonly string[];
  readonly creditor: PrintParty;
  readonly debtor: PrintParty;
  readonly contract: {
    readonly number: string;
    readonly date: string;
    readonly directionLabel: string;
    readonly subject: string | null;
    readonly court: string | null;
    readonly specification: string | null;
  };
  readonly obligation: {
    readonly typeLabel: string;
    readonly descr: string;
    readonly amount: string | null;
    readonly manualStatusReason: string | null;
    /** Дата правового решения «Спор»/«Снято» (D-6). */
    readonly manualStatusDate: string | null;
    readonly disputeCaseNo: string | null;
  };
  /**
   * Состояние обязательства на ДАТУ ФОРМИРОВАНИЯ (`asOf`), а не на дату отсечки расчёта: остаток
   * и статус вычисляются из полного списка событий (Р-2). `sameDate` — даты совпали, отдельная
   * подпись блока не нужна.
   */
  readonly current: {
    readonly asOf: string;
    readonly remaining: string | null;
    readonly statusLabel: string;
    readonly sameDate: boolean;
  };
  readonly accrual: {
    readonly base: string | null;
    readonly dueComputed: string | null;
    readonly dueShifted: boolean;
    readonly dueUsed: string | null;
    readonly override: { readonly date: string; readonly reason: string } | null;
    readonly from: string | null;
    readonly to: string | null;
    readonly stoppedByDoneDate: boolean;
    readonly doneDate: string | null;
    /** Начисление оборвано снятием с контроля (D-6): признак и дата приходят готовыми с сервера. */
    readonly stoppedByCancelled: boolean;
    readonly cancelledDate: string | null;
    /** Периода начисления нет: последний день раньше первого — диапазон не печатаем. */
    readonly noAccrualPeriod: boolean;
  };
  readonly sections: readonly PrintSection[];
  readonly total: {
    readonly kind: "penalty" | "interest395" | null;
    readonly label: string;
    readonly amount: string | null;
  };
  readonly notices: readonly CalcNotice[];
  readonly claim: PrintClaimInfo | null;
  /** Штамп «ОТМЕНЕНА» через всю форму: дата (может отсутствовать) и причина отмены претензии. */
  readonly cancellation: { readonly date: string | null; readonly reason: string } | null;
  readonly on: string;
  /** Момент формирования (ISO) — показывается в поясе организации (Омск, Р-16). */
  readonly printedAt: string;
}

/** Печатная форма расчёта по обязательству (точка входа «а»). Без `on` — «сегодня» организации. */
export function fetchObligationCalcPrint(
  obligationId: string,
  on?: string,
): Promise<ApiResult<CalcPrintForm>> {
  return send(
    `/api/obligations/${encodeURIComponent(obligationId)}/calc-print${onParam(on)}`,
    { method: "GET", headers: { Accept: "application/json" } },
    (b) => (b as { form?: CalcPrintForm } | null)?.form,
  );
}

/** Печатная форма расчёта к претензии (точка входа «б»): дата отсечки — из самой претензии. */
export function fetchClaimCalcPrint(claimId: string): Promise<ApiResult<CalcPrintForm>> {
  return send(
    `/api/claims/${encodeURIComponent(claimId)}/calc-print`,
    { method: "GET", headers: { Accept: "application/json" } },
    (b) => (b as { form?: CalcPrintForm } | null)?.form,
  );
}

/** Роли строки 4.3 «Претензии, перевод в спор, снятие с контроля» — подсказка UI. */
const LEGAL_ROLES: readonly Role[] = ["lawyer", "admin"];

export function canManageLegal(roles: readonly Role[]): boolean {
  return roles.some((r) => LEGAL_ROLES.includes(r));
}

const JSON_HEADERS = { "Content-Type": "application/json", Accept: "application/json" } as const;

async function toFailure(res: Response): Promise<Extract<ApiResult<never>, { ok: false }>> {
  if (res.status === 401) {
    return { ok: false, reason: "unauthorized", message: "Сессия истекла. Войдите заново." };
  }
  const body = (await res.json().catch(() => null)) as { message?: string } | null;
  const message = body?.message;
  if (res.status === 403) {
    return {
      ok: false,
      reason: "forbidden",
      message: message ?? "Претензии и правовые статусы ведёт юрист.",
    };
  }
  if (res.status === 409)
    return { ok: false, reason: "conflict", message: message ?? "Конфликт данных." };
  if (res.status === 404)
    return { ok: false, reason: "network", message: message ?? "Запись не найдена." };
  if (res.status === 400)
    return { ok: false, reason: "validation", message: message ?? "Проверьте введённые данные." };
  return { ok: false, reason: "network", message: message ?? "Не удалось выполнить операцию." };
}

async function send<T>(
  url: string,
  init: RequestInit,
  pick: (body: unknown) => T | undefined,
): Promise<ApiResult<T>> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: "same-origin", ...init });
  } catch {
    return { ok: false, reason: "network", message: "Сервер недоступен. Повторите позже." };
  }
  if (!res.ok) return toFailure(res);
  const body: unknown = await res.json().catch(() => null);
  const value = pick(body);
  if (value === undefined) {
    return { ok: false, reason: "network", message: "Неожиданный ответ сервера." };
  }
  return { ok: true, value };
}

/** Дата расчёта в строку запроса: пусто — сервер возьмёт «сегодня» организации (Р-16). */
function onParam(on?: string): string {
  return on === undefined || on === "" ? "" : `?on=${encodeURIComponent(on)}`;
}

/** Правовая карточка обязательства: расчёт, давность, претензии. Просмотр — по матрице 4.3. */
export function fetchObligationLegal(
  obligationId: string,
  on?: string,
): Promise<ApiResult<ObligationLegal>> {
  return send(
    `/api/obligations/${encodeURIComponent(obligationId)}/legal${onParam(on)}`,
    { method: "GET", headers: { Accept: "application/json" } },
    (b) => (b as { legal?: ObligationLegal } | null)?.legal,
  );
}

export interface ClaimInput {
  readonly sentDate: string;
  readonly outNumber: string;
  readonly answerDays?: number;
  readonly amount?: string;
  readonly calcOn?: string;
}

export function createClaim(obligationId: string, input: ClaimInput): Promise<ApiResult<Claim>> {
  return send(
    `/api/obligations/${encodeURIComponent(obligationId)}/claims`,
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify(input) },
    (b) => (b as { claim?: Claim } | null)?.claim,
  );
}

export function setClaimResult(
  claimId: string,
  input: { result: ClaimResult; resultDate?: string; resultNote?: string },
): Promise<ApiResult<Claim>> {
  return send(
    `/api/claims/${encodeURIComponent(claimId)}/result`,
    { method: "PATCH", headers: JSON_HEADERS, body: JSON.stringify(input) },
    (b) => (b as { claim?: Claim } | null)?.claim,
  );
}

/** Отменить ошибочно заведённую претензию (сторно-паттерн E8a): правка реквизитов невозможна. */
export function reverseClaim(claimId: string, reason: string): Promise<ApiResult<Claim>> {
  return send(
    `/api/claims/${encodeURIComponent(claimId)}/reverse`,
    { method: "POST", headers: JSON_HEADERS, body: JSON.stringify({ reason }) },
    (b) => (b as { claim?: Claim } | null)?.claim,
  );
}

/**
 * Ручной статус: «Спор» (с № дела) / «Снято» / возврат под обычный контроль.
 * `date` — дата решения (D-6): обязательна вместе со статусом, будущей быть не может (проверяет
 * сервер по календарному дню организации). Датой СНЯТИЯ обрывается начисление санкций.
 */
export function setManualStatus(
  obligationId: string,
  input: {
    status: "dispute" | "cancelled" | null;
    caseNo?: string;
    reason?: string;
    date?: string;
  },
): Promise<ApiResult<Obligation>> {
  return send(
    `/api/obligations/${encodeURIComponent(obligationId)}/manual-status`,
    { method: "PATCH", headers: JSON_HEADERS, body: JSON.stringify(input) },
    (b) => (b as { obligation?: Obligation } | null)?.obligation,
  );
}

/** Дата, от которой ведётся начисление санкций (Р-24): мораторий, рассрочка, иное. */
export function setAccrualDue(
  obligationId: string,
  input: { date: string | null; reason?: string },
): Promise<ApiResult<Obligation>> {
  return send(
    `/api/obligations/${encodeURIComponent(obligationId)}/accrual-due`,
    { method: "PATCH", headers: JSON_HEADERS, body: JSON.stringify(input) },
    (b) => (b as { obligation?: Obligation } | null)?.obligation,
  );
}

/**
 * Роли, которым доступно ЧТЕНИЕ правового блока — расчёта санкций, претензий и давности
 * (право `legal:view`). Делопроизводителя здесь НЕТ: заказчик ответил на В28 «нет» —
 * суммы начисленных санкций ему не показываются (CR-1 от 06.08.2026).
 *
 * Это подсказка интерфейса; авторитетный запрет — на сервере (правило 5): без права он
 * отвечает 403 и на карточку расчёта, и на печатную форму.
 */
export const LEGAL_VIEW_ROLES: readonly Role[] = [
  "commercial",
  "accounting",
  "lawyer",
  "director",
  "admin",
];

export function canViewLegal(roles: readonly Role[]): boolean {
  return roles.some((r) => LEGAL_VIEW_ROLES.includes(r));
}
